import React, { useCallback, useMemo, useState } from "react";
import {
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Icon,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { visuallyHidden } from "@mui/utils";
import styled from "@emotion/styled";
import MDInput from "components/MDInput";

const Label = styled(FormControlLabel)((props) => ({
  [`& .MuiFormControlLabel-label`]: {
    fontFamily: "Helvetica",
    fontSize: "0.8rem",
    fontWeight: props?.bold ? 800 : 400,
    lineHeight: "1rem",
  },
}));

function descendingComparator(a, b, orderBy) {
  let valueA = a[orderBy];
  let valueB = b[orderBy];
  if (!valueA) return 1;
  if (!valueB) return -1;

  if (["averagePoints", "totalAvg", "totalCount", "totalPoint"].includes(orderBy)) {
    valueA = parseFloat(valueA);
    valueB = parseFloat(valueB);
  } else {
    valueA = valueA?.toLowerCase();
    valueB = valueB?.toLowerCase();
  }
  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const SortableLabel = React.memo(
  ({ labelKey, children, order, orderBy, handleRequestSort }) => (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{ cursor: "pointer" }}
      onClick={() => handleRequestSort(order)}
    >
      {children}
      {orderBy === labelKey ? (
        <>
          <Icon fontSize="small" color="disabled" sx={{ marginLeft: 1 }}>
            {order === "desc" ? "arrow_downward" : "arrow_upward"}
          </Icon>
          <MDBox component="span" sx={visuallyHidden}>
            {order === "desc" ? "sorted descending" : "sorted ascending"}
          </MDBox>
        </>
      ) : null}
    </MDBox>
  ),
  (prevProps, nextProps) =>
    prevProps.order === nextProps.order &&
    prevProps.orderBy === nextProps.orderBy &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

const MetricsView = ({
  data,
  columnsWithNames,
  label,
  objectKey,
  handleTableFilterChange,
  includeSearch = false,
}) => {
  const [searchByName, setSearchByName] = useState("");
  const [order, setOrder] = useState("asc");
  const orderBy = "name";

  const handleSearchFilter = (user) => {
    if (!searchByName) {
      return true;
    }
    return user?.name?.toLowerCase().includes(searchByName.toLowerCase());
  };

  const visibleRows = useMemo(() => {
    let allDataRecord = [];
    const filteredData = data?.filter(handleSearchFilter);
    const findAllDataIndex = filteredData?.findIndex((obj) => obj.id === "all");
    if (findAllDataIndex > -1) allDataRecord = filteredData?.splice(findAllDataIndex, 1);
    return [...allDataRecord, ...filteredData.sort(getComparator(order, orderBy))];
  }, [order, orderBy, searchByName, handleSearchFilter, data]);

  const handleRequestSort = useCallback((orderValue) => {
    setOrder(orderValue === "asc" ? "desc" : "asc");
  }, []);

  return (
    <Card>
      <MDBox display="flex" alignItems="center" m={2}>
        <SortableLabel
          labelKey="name"
          order={order}
          orderBy={orderBy}
          handleRequestSort={handleRequestSort}
        >
          <MDTypography>{label}</MDTypography>
        </SortableLabel>
        {includeSearch ? (
          <MDInput
            sx={{ marginLeft: 4 }}
            value={searchByName}
            onChange={(event) => setSearchByName(event.target.value)}
            label="Search"
          />
        ) : null}
      </MDBox>
      <MDBox sx={{ maxHeight: "45vh", overflowY: "scroll" }}>
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <FormGroup row sx={{ display: "flex", flexDirection: "column" }}>
            {visibleRows?.map((option, index) => {
              const isChecked = columnsWithNames?.some((obj) => obj.id === option.id);
              return (
                <>
                  <Label
                    key={option.id}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleTableFilterChange(objectKey, option.id, e.target.checked)
                        }
                        name={option.id}
                        sx={{ display: "none" }}
                      />
                    }
                    label={
                      <MDBox display="flex" alignItems="center">
                        {option.name}
                        {isChecked ? <CancelIcon /> : null}
                      </MDBox>
                    }
                    labelPlacement="top"
                    bold={isChecked}
                  />
                  {index !== visibleRows.length - 1 ? (
                    <Divider sx={{ margin: 0, padding: 0 }} />
                  ) : null}
                </>
              );
            })}
          </FormGroup>
        </FormControl>
      </MDBox>
    </Card>
  );
};

export default React.memo(MetricsView);
